import type { HttpError } from "@repo/helpers";
import type { AxiosRequestConfig } from "axios";

import { Delete as HttpDelete, Get as HttpGet, Post as HttpPost, Put as HttpPut } from "@repo/helpers";
import { useCallback } from "react";

import { useUserContext } from "../contexts/userContext";

/** Calls `onError` when the request fails and still hands the rejection to the caller. */
function notifyOnError<T>(request: Promise<T>, onError: (error: HttpError) => void) {
	request.catch(onError);
	return request;
}

/** MVC requests that send the user back to the login page when the server returns 401. */
export function useTLPCloudApi() {
	const { onAuthError } = useUserContext();

	const httpError = useCallback(
		(error: HttpError) => {
			switch (error.status) {
				case 401:
					onAuthError();
					break;
			}
		},
		[onAuthError],
	);

	const Get = useCallback(
		function <TOut>(controller: string, action: string, params: object = {}, config?: AxiosRequestConfig) {
			return notifyOnError(HttpGet<TOut>(controller, action, params, config), httpError);
		},
		[httpError],
	);
	const Post = useCallback(
		// oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- callers pass explicit <TOut, TIn> type arguments
		function <TOut, TIn = any>(
			controller: string,
			action: string,
			params: object = {},
			data?: TIn,
			config?: AxiosRequestConfig,
		) {
			return notifyOnError(HttpPost<TOut, TIn>(controller, action, params, data, config), httpError);
		},
		[httpError],
	);
	const Put = useCallback(
		// oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- callers pass explicit <TOut, TIn> type arguments
		function <TOut, TIn = any>(
			controller: string,
			action: string,
			params: object = {},
			data?: TIn,
			config?: AxiosRequestConfig,
		) {
			return notifyOnError(HttpPut<TOut, TIn>(controller, action, params, data, config), httpError);
		},
		[httpError],
	);
	const Delete = useCallback(
		function <TOut>(controller: string, action: string, id: number, config?: AxiosRequestConfig) {
			return notifyOnError(HttpDelete<TOut>(controller, action, id, config), httpError);
		},
		[httpError],
	);

	return { Get, Post, Put, Delete };
}

/** `useTLPCloudApi` bound to one MVC controller. */
export function useControllerApi(controller: string) {
	const { Get: cloudGet, Post: cloudPost, Put: cloudPut, Delete: cloudDelete } = useTLPCloudApi();
	const Get = useCallback(
		function <TOut>(action: string, params: object = {}, config?: AxiosRequestConfig) {
			return cloudGet<TOut>(controller, action, params, config);
		},
		[cloudGet, controller],
	);
	const Post = useCallback(
		// oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- callers pass explicit <TOut, TIn> type arguments
		function <TOut, TIn = any>(action: string, params: object = {}, data?: TIn, config?: AxiosRequestConfig) {
			return cloudPost<TOut, TIn>(controller, action, params, data, config);
		},
		[cloudPost, controller],
	);
	const Put = useCallback(
		// oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- callers pass explicit <TOut, TIn> type arguments
		function <TOut, TIn = any>(action: string, params: object = {}, data?: TIn, config?: AxiosRequestConfig) {
			return cloudPut<TOut, TIn>(controller, action, params, data, config);
		},
		[cloudPut, controller],
	);
	const Delete = useCallback(
		function <TOut>(action: string, id: number, config?: AxiosRequestConfig) {
			return cloudDelete<TOut>(controller, action, id, config);
		},
		[cloudDelete, controller],
	);

	return { Get, Post, Put, Delete };
}
